<template>
  <div class="customization-upload-image-wrapper">
    <RadioGroup
      v-if="isShowOptions"
      type="image"
      :group="id"
      :options="options"
      :loading="loading"
      v-model="templateIdSelected"
      class="mb-sm"
    ></RadioGroup>
    <CustomFileUpload :id="id"></CustomFileUpload>
    <span class="tooltip-image-processing" v-show="loading">
      {{ imageProcessingText }}
      {{ percentProcessed ? `${percentProcessed}%` : '' }}
    </span>
    <span
      class="remove-custom-upload-image"
      v-show="isSuccessUpload && !loading"
      @click="removeImage"
    >
      X
    </span>
    <span
      class="customization-upload-image-wrapper-message"
      v-show="isShowMessage && !isRemoveImage"
      :class="messageClasses"
    >
      {{ messageImageUpload }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed, h, onMounted, ref, watch } from 'vue'
import FileUpload from '@/ui-kits/FileSelect.vue'
import RadioGroup from '@/ui-kits/RadioGroup.vue'
import Logger from '@/services/log'
import useCustomizationStore from '@/store/customization'
import { useRoute } from 'vue-router'
import {
  CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS,
  EVENT_CUSTOM_UPLOAD_IMAGE,
  QUERY_STRING_NO_CACHE,
} from '@/utils'
import EventBus from '@/services/eventbus'
import { CustomizationTemplate } from '@/types'
import { useProductCustomization } from './composables/ProductCustomization'

const customizationStore = useCustomizationStore()
const route = useRoute()
const {
  convertImageLinkToFileList,
  changeFileImageCustomily,
  startUpload,
  finishUpload,
  percentProcessed,
} = useProductCustomization()

const props = withDefaults(
  defineProps<{
    id: string
    inputFileCustomily: Element
    selectImageButtonCustomily: Element
    removeImageButtonCustomily: HTMLElement
    customizationTemplate: CustomizationTemplate
    optionSortId: string
  }>(),
  {}
)

let options = props.customizationTemplate.templates.map((option) => ({
  id: option.template_id,
  name: option.name || option.title,
  image: option.image,
  value: option.template_id,
}))

const messageImageUpload = computed(() =>
  isSuccessUpload.value ? successfullUploadMessage : errorUploadMessage.value
)
const messageClasses = computed(() =>
  isSuccessUpload.value
    ? textPrimaryClass
    : `${textSecondaryClass} ${CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS}-message--error`
)

const templateIdDefault = computed(() => {
  const templateIdOriginal = '0000'
  const index = options.findIndex(
    (option) => option.value === templateIdOriginal
  )
  return index > -1 ? templateIdOriginal : options[0].value
})

const isShowOptions = computed(() => options.length > 1)

let loading = ref(false)
let isSuccessUpload = ref(false)
let isShowMessage = ref(false)
let isRemoveImage = ref(false)
const imageProcessingText = 'Processing'
const successfullUploadMessage = 'Successful upload!'
let errorUploadMessage = ref('Error upload!')
const textPrimaryClass = 'text-primary'
const textSecondaryClass = 'text-secondary'
const sibFileNameStyleTransferImagePrefix = 'sib-file-name-style-transfer-image'
const errorTransferImageMessage =
  'The Image Style could not be done with this image. Please choose another image or use different way of style'

let templateIdSelected = ref(templateIdDefault.value)
let imageSelected = ref('')

onMounted(() => {
  if (customizationStore.styleTransfered.length) {
    const styleTransferedItem = customizationStore.styleTransfered.find(
      (item) => item.optionSortId === props.optionSortId
    )
    if (styleTransferedItem) {
      templateIdSelected.value = styleTransferedItem.templateId
      imageSelected.value = styleTransferedItem.image
    }
  }

  watch(
    () => templateIdSelected.value,
    async (value) => {
      try {
        if (!value || !imageSelected.value) return
        loading.value = true
        isSuccessUpload.value = false
        isShowMessage.value = false

        const payload = {
          image: imageSelected.value,
          templateId: value,
          id: props.customizationTemplate.id,
        }
        const imageTransferedLink = await transferImage(payload)
        if (!imageTransferedLink) {
          throw {
            message: errorTransferImageMessage,
          }
        }

        const fileName = `${sibFileNameStyleTransferImagePrefix}-${templateIdSelected.value}-${props.customizationTemplate.id}`
        const imageFileList = await convertImageLinkToFileList(
          imageTransferedLink!,
          fileName
        )
        changeFileImageCustomily(imageFileList, props.inputFileCustomily)

        isSuccessUpload.value = true
        customilyUpLoadingHandler()
        saveStyleTransfered()
      } catch (err: any) {
        errorUploadMessage.value = err.message ?? errorUploadMessage.value
        loading.value = false
        isSuccessUpload.value = false
        isShowMessage.value = true

        Logger.log('Error when transfer style image', err)
      }
    }
  )
  watch(
    () => loading.value,
    (value) => {
      EventBus.trigger(EVENT_CUSTOM_UPLOAD_IMAGE, value)
      if (value) startUpload()
      else finishUpload()
    }
  )
})

async function transferImage({
  image,
  templateId,
  id,
}: {
  image: string
  templateId: string
  id: number
}) {
  if (!image || !templateId || !id) return
  const payload = {
    image,
    templateId,
    id,
    isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
  }
  const imageTransferedLink = await customizationStore.transferImage(payload)
  return imageTransferedLink
}

const CustomFileUpload = ({ id }: { id: string }) => {
  let tag: any = FileUpload
  const propsCustomFileUpload: {
    [key: string]: any
  } = {}

  propsCustomFileUpload.id = id
  propsCustomFileUpload.loading = loading.value
  propsCustomFileUpload.onChange = onChange

  async function onChange(event: Event) {
    const input = event.target as HTMLInputElement
    if (!input.files) return
    const file = input.files[0]
    if (!file) return

    loading.value = true
    isSuccessUpload.value = false
    isShowMessage.value = false
    isRemoveImage.value = false

    const payload = {
      file,
      id: props.customizationTemplate.id,
      isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
    }
    customizationStore
      .uploadImageTransferDefault(payload)
      .then(async (response) => {
        if (response.success === false) {
          throw response
        }
        const link = response
        imageSelected.value = link

        const payload = {
          image: link!,
          templateId: templateIdSelected.value,
          id: props.customizationTemplate.id,
        }
        const imageTransferedLink = await transferImage(payload)
        if (!imageTransferedLink) {
          throw {
            message: errorTransferImageMessage,
          }
        }

        const fileName = `${sibFileNameStyleTransferImagePrefix}-${templateIdSelected.value}-${props.customizationTemplate.id}`
        const imageFileList = await convertImageLinkToFileList(
          imageTransferedLink!,
          fileName
        )
        changeFileImageCustomily(imageFileList, props.inputFileCustomily)
        isSuccessUpload.value = true
        saveStyleTransfered()
      })
      .catch((err) => {
        errorUploadMessage.value = err.message ?? errorUploadMessage.value
        isSuccessUpload.value = false
        Logger.log('Error when uploading image', err)
      })
      .finally(() => {
        if (isSuccessUpload.value) {
          customilyUpLoadingHandler()
        } else {
          loading.value = false
          isShowMessage.value = true
        }
      })
    input.value = ''
  }

  return h(tag, propsCustomFileUpload)
}

function customilyUpLoadingHandler() {
  const timeout = setTimeout(() => {
    const isCustomilyUpLoading =
      props.selectImageButtonCustomily.classList.contains('disabled')
    if (isCustomilyUpLoading) {
      customilyUpLoadingHandler()
    } else {
      loading.value = false
      isShowMessage.value = true
      clearTimeout(timeout)
    }
  }, 200)
}

function removeImage() {
  if (!props.removeImageButtonCustomily) return
  props.removeImageButtonCustomily.click()
  imageSelected.value = ''
  isSuccessUpload.value = false
  isRemoveImage.value = true
}

function saveStyleTransfered() {
  const index = customizationStore.styleTransfered.findIndex(
    (item) => item.optionSortId == props.optionSortId
  )
  const styleTransferedItem = {
    optionSortId: props.optionSortId,
    templateId: templateIdSelected.value,
    image: imageSelected.value,
  }
  if (index > -1) {
    customizationStore.styleTransfered.splice(index, 1, styleTransferedItem)
  } else {
    customizationStore.styleTransfered.push(styleTransferedItem)
  }
}
</script>
